import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { CampaignClass, TCampaignGroup } from '@feathr/blackbox';
import { CampaignGroupMap } from '@feathr/blackbox';
import { Select } from '@feathr/components';
import {
  useCampaignPermissions,
  useDripPermissions,
  useGoogleAdsPermissions,
} from '@feathr/extender/hooks';

import type { ITypeOption } from './CampaignTypeSelect.utils';
import { getOptions } from './CampaignTypeSelect.utils';

interface IAccessConditions {
  showGoogleAds: boolean;
  showDripCampaigns: boolean;
}

interface IProps {
  campaignGroup: TCampaignGroup;
  onChange: (value?: Array<CampaignClass | 'monetization'>) => void;
  value?: Array<CampaignClass | 'monetization'>;
}

function CampaignTypeSelect({ campaignGroup, value = [], onChange }: IProps): JSX.Element {
  const { t } = useTranslation();
  const showGoogleAds = useGoogleAdsPermissions();
  const showDripCampaigns = useDripPermissions();
  const permissions = useCampaignPermissions();

  const conditions: IAccessConditions = {
    showGoogleAds,
    showDripCampaigns,
  };

  const options = getOptions({ permissions, conditions });

  const typeValue = value.reduce((acc, cur) => {
    const option = options.find(({ id }) => id === cur);
    if (option) {
      acc.push(option);
    }
    return acc;
  }, [] as ITypeOption[]);

  const sectionOptions = options.filter(({ id }) => {
    if (id === 'monetization') {
      return campaignGroup === 'all';
    }
    return CampaignGroupMap[campaignGroup].includes(id);
  });

  function handleSelectTypes(types: ITypeOption[] = []): void {
    onChange(types.map(({ id }) => id));
  }

  return (
    <Select<ITypeOption>
      id={'typeSelect'}
      isClearable={true}
      isMulti={true}
      label={t('Type')}
      name={'select-campaign-type'}
      onClear={handleSelectTypes}
      onSelectMulti={handleSelectTypes}
      options={sectionOptions}
      placeholder={t('All campaign types')}
      value={typeValue}
    />
  );
}

export default observer(CampaignTypeSelect);
